import { JobFilterSet, type JobFilterSetDTO } from '@ironsight/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  createJobFilterSet,
  deleteJobFilterSet,
  getJobFilterSets,
  updateJobFilterSet,
} from '@/api/settings'

export const useJobFilterSetsQueryKey = ['filter_sets']

export function useJobFilterSetsQuery() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: useJobFilterSetsQueryKey,
    queryFn: async ({ signal }) => {
      const response = await getJobFilterSets(signal)
      return response.data.map((json) => new JobFilterSet(json))
    },
    retry: false,
    keepPreviousData: false,
  })

  const createMutation = useMutation({
    mutationFn: (filterSetDTO: JobFilterSetDTO) => createJobFilterSet(filterSetDTO),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: useJobFilterSetsQueryKey,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (filterSetId: string) => deleteJobFilterSet(filterSetId),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: useJobFilterSetsQueryKey,
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, filterSetDTO }: { id: string; filterSetDTO: JobFilterSetDTO }) =>
      updateJobFilterSet(id, filterSetDTO),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: useJobFilterSetsQueryKey,
      })
    },
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,

    createMutation,
    deleteMutation,
    updateMutation,
  }
}
